import React, { useState, useEffect } from 'react';
import xService from '../services/xService';
import { promptService } from '../services/promptService';

const XNotificationForm = ({ 
  notification, 
  onSubmit, 
  onCancel,
  isSubmitting 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    search_config_ids: [],
    prompt_name: '',
    ms_teams_channel: '',
    schedule: {
      cadence_value: 1,
      cadence_unit: 'hours',
      start_time: '08:00',
      end_time: '17:00',
      all_day: false,
      run_once_daily: false,
      days_of_week: [],
      is_active: true
    },
    is_active: true
  });

  const [searches, setSearches] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [scheduleMode, setScheduleMode] = useState('cadence'); // 'cadence' | 'once_daily'

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (notification) {
      setFormData({
        name: notification.name || '',
        description: notification.description || '',
        search_config_ids: notification.search_config_ids || [],
        prompt_name: notification.prompt_name || '',
        ms_teams_channel: notification.ms_teams_channel || '',
        schedule: notification.schedule || {
          cadence_value: 1,
          cadence_unit: 'hours',
          start_time: '08:00',
          end_time: '17:00',
          all_day: false,
          run_once_daily: false,
          days_of_week: [],
          is_active: true
        },
        is_active: notification.is_active !== undefined ? notification.is_active : true
      });
      setScheduleMode(notification.schedule?.run_once_daily ? 'once_daily' : 'cadence');
    }
  }, [notification]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [searchesData, promptsData, channelsData] = await Promise.all([
        xService.getAllSearches(),
        promptService.getPrompts(),
        xService.getMSTeamsChannels()
      ]);
      const sortedSearches = (searchesData.searches || []).slice().sort((a, b) => a.name.localeCompare(b.name));
      setSearches(sortedSearches);
      // Filter prompts to only include those starting with "X_"
      const filteredPrompts = (promptsData.prompts || []).filter(prompt => 
        prompt.name && prompt.name.startsWith('X_')
      );
      setPrompts(filteredPrompts);
      setChannels(channelsData.channels || []);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load searches and prompts: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleScheduleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handleScheduleNumberChange = (e) => {
    const { name, value } = e.target;
    const numValue = parseInt(value) || 1;
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [name]: Math.min(50, Math.max(1, numValue))
      }
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const days = prev.schedule.days_of_week.includes(day)
        ? prev.schedule.days_of_week.filter(d => d !== day)
        : [...prev.schedule.days_of_week, day].sort();
      return { 
        ...prev, 
        schedule: {
          ...prev.schedule,
          days_of_week: days
        }
      };
    });
  };

  const handleSearchToggle = (searchId) => {
    setFormData(prev => {
      const searches = prev.search_config_ids.includes(searchId)
        ? prev.search_config_ids.filter(id => id !== searchId)
        : [...prev.search_config_ids, searchId];
      return { ...prev, search_config_ids: searches };
    });
  };

  const handleSelectAll = () => {
    const filteredIds = searches
      .filter(s => s.name.toLowerCase().includes(searchFilter.toLowerCase()))
      .map(s => s.id);
    const allSelected = filteredIds.every(id => formData.search_config_ids.includes(id));
    setFormData(prev => ({
      ...prev,
      search_config_ids: allSelected
        ? prev.search_config_ids.filter(id => !filteredIds.includes(id))
        : [...new Set([...prev.search_config_ids, ...filteredIds])]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.search_config_ids.length === 0) {
      alert('Please select at least one search configuration');
      return;
    }
    
    if (!formData.prompt_name) {
      alert('Please select a prompt');
      return;
    }
    
    if (!formData.ms_teams_channel) {
      alert('Please select a MS Teams channel');
      return;
    }
    
    onSubmit(formData);
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Basic Info */}
      <div className="mb-3">
        <label className="form-label fw-bold">Name *</label>
        <input
          type="text"
          className="form-control"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          maxLength="100"
          placeholder="e.g., Daily Market Summary"
        />
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold">Description</label>
        <textarea
          className="form-control"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="2"
          maxLength="500"
          placeholder="Optional description of this notification"
        />
      </div>

      {/* Search Selection */}
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <label className="form-label fw-bold mb-0">Search Configurations *</label>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleSelectAll}
            disabled={searches.length === 0}
          >
            {searches
              .filter(s => s.name.toLowerCase().includes(searchFilter.toLowerCase()))
              .every(s => formData.search_config_ids.includes(s.id)) && searches.length > 0
              ? 'Deselect All'
              : 'Select All'}
          </button>
        </div>
        <input
          type="text"
          className="form-control form-control-sm mb-2"
          placeholder="Filter searches..."
          value={searchFilter}
          onChange={e => setSearchFilter(e.target.value)}
        />
        <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {searches.length === 0 ? (
            <p className="text-muted mb-0">No searches available</p>
          ) : (
            searches
              .filter(s => s.name.toLowerCase().includes(searchFilter.toLowerCase()))
              .map(search => (
                <div key={search.id} className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`search-${search.id}`}
                    checked={formData.search_config_ids.includes(search.id)}
                    onChange={() => handleSearchToggle(search.id)}
                  />
                  <label className="form-check-label" htmlFor={`search-${search.id}`}>
                    {search.name}
                    {search.description && (
                      <small className="text-muted d-block">{search.description}</small>
                    )}
                  </label>
                </div>
              ))
          )}
        </div>
        <small className="text-muted">
          Select which search results to include in the notification
        </small>
      </div>

      {/* Prompt Selection */}
      <div className="mb-3">
        <label className="form-label fw-bold">Prompt *</label>
        <select
          className="form-select"
          name="prompt_name"
          value={formData.prompt_name}
          onChange={handleChange}
          required
        >
          <option value="">Select a prompt...</option>
          {prompts.map(prompt => (
            <option key={prompt.prompt_id} value={prompt.app_id}>
              {prompt.name}
            </option>
          ))}
        </select>
        <small className="text-muted">
          Select the analysis prompt to use from Prompt Manager (X_ prompts only)
        </small>
      </div>

      {/* MS Teams Channel */}
      <div className="mb-3">
        <label className="form-label fw-bold">MS Teams Channel *</label>
        <select
          className="form-select"
          name="ms_teams_channel"
          value={formData.ms_teams_channel}
          onChange={handleChange}
          required
        >
          <option value="">Select a channel...</option>
          {channels.map(channel => (
            <option key={channel.id} value={channel.name}>
              {channel.name}
            </option>
          ))}
        </select>
        <small className="text-muted">
          Select the MS Teams channel where notifications will be sent
        </small>
      </div>

      {/* Schedule Configuration */}
      <hr />
      <h6 className="mb-3">Schedule Configuration</h6>

      {/* Schedule Mode Toggle */}
      <div className="mb-3">
        <label className="form-label fw-bold">Schedule Mode</label>
        <div className="btn-group w-100" role="group">
          <input
            type="radio"
            className="btn-check"
            name="scheduleMode"
            id="mode-cadence"
            checked={scheduleMode === 'cadence'}
            onChange={() => {
              setScheduleMode('cadence');
              setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, run_once_daily: false } }));
            }}
          />
          <label className="btn btn-outline-primary" htmlFor="mode-cadence">
            Repeating Cadence
          </label>
          <input
            type="radio"
            className="btn-check"
            name="scheduleMode"
            id="mode-once-daily"
            checked={scheduleMode === 'once_daily'}
            onChange={() => {
              setScheduleMode('once_daily');
              setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, run_once_daily: true, all_day: false } }));
            }}
          />
          <label className="btn btn-outline-primary" htmlFor="mode-once-daily">
            Once Daily
          </label>
        </div>
        <small className="text-muted">
          {scheduleMode === 'once_daily'
            ? 'Runs exactly once per day at the specified time'
            : 'Runs repeatedly at a cadence interval within a time window'}
        </small>
      </div>

      {scheduleMode === 'cadence' && (
        <div className="mb-3">
          <label className="form-label fw-bold">Cadence</label>
          <div className="row g-2">
            <div className="col-md-6">
              <input
                type="number"
                className="form-control"
                name="cadence_value"
                value={formData.schedule.cadence_value}
                onChange={handleScheduleNumberChange}
                min="1"
                max="50"
                required
                placeholder="Number (1-50)"
              />
            </div>
            <div className="col-md-6">
              <select
                className="form-select"
                name="cadence_unit"
                value={formData.schedule.cadence_unit}
                onChange={handleScheduleChange}
                required
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
              </select>
            </div>
          </div>
          <small className="text-muted">
            How often to check for new results and send notification
          </small>
        </div>
      )}

      <div className="mb-3">
        <label className="form-label fw-bold">
          {scheduleMode === 'once_daily' ? 'Run Time (EST)' : 'Start Time (EST)'}
        </label>
        <input
          type="time"
          className="form-control"
          name="start_time"
          value={formData.schedule.start_time}
          onChange={handleScheduleChange}
          required
        />
        <small className="text-muted">
          {scheduleMode === 'once_daily'
            ? 'Time of day to run the notification (EST)'
            : 'Time when the schedule begins running each day'}
        </small>
      </div>

      {scheduleMode === 'cadence' && (
        <>
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="all_day"
              name="all_day"
              checked={formData.schedule.all_day}
              onChange={handleScheduleChange}
            />
            <label className="form-check-label" htmlFor="all_day">
              Run all day (24/7 at cadence interval)
            </label>
            <br />
            <small className="text-muted">
              {formData.schedule.all_day
                ? `Will run continuously every ${formData.schedule.cadence_value} ${formData.schedule.cadence_value === 1 ? formData.schedule.cadence_unit.slice(0, -1) : formData.schedule.cadence_unit}`
                : `Will run within the time window from ${formData.schedule.start_time} to ${formData.schedule.end_time} at the cadence interval`}
            </small>
          </div>

          {!formData.schedule.all_day && (
            <div className="mb-3">
              <label className="form-label fw-bold">End Time (EST)</label>
              <input
                type="time"
                className="form-control"
                name="end_time"
                value={formData.schedule.end_time}
                onChange={handleScheduleChange}
                required={!formData.schedule.all_day}
              />
              <small className="text-muted">
                Time when the schedule stops running each day (must be after start time)
              </small>
            </div>
          )}
        </>
      )}

      <div className="mb-3">
        <label className="form-label fw-bold">Days of Week</label>
        <div className="btn-group w-100" role="group">
          {dayNames.map((day, index) => (
            <React.Fragment key={index}>
              <input
                type="checkbox"
                className="btn-check"
                id={`notif-day-${index}`}
                checked={formData.schedule.days_of_week.includes(index)}
                onChange={() => handleDayToggle(index)}
              />
              <label 
                className={`btn btn-outline-primary ${formData.schedule.days_of_week.includes(index) ? 'active' : ''}`}
                htmlFor={`notif-day-${index}`}
              >
                {day}
              </label>
            </React.Fragment>
          ))}
        </div>
        <small className="text-muted">
          Leave all unchecked to run every day
        </small>
      </div>

      <hr />

      <div className="mb-3 form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id="notification_is_active"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
        />
        <label className="form-check-label" htmlFor="notification_is_active">
          Notification is active
        </label>
        <small className="text-muted d-block mt-1">
          When checked, this notification will run according to the schedule above
        </small>
      </div>

      <div className="d-flex gap-2 justify-content-end">
        <button 
          type="button" 
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            notification ? 'Update Notification' : 'Create Notification'
          )}
        </button>
      </div>
    </form>
  );
};

export default XNotificationForm;
